import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Trash2,
  Eye,
  MoreVertical,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Plus,
} from "lucide-react";
import axios from "axios";
import { useMemo } from "react";
import { getToken } from "../../utils/tokenUtils";
import ReactPaginate from "react-paginate";
import DropdownMenu from "../../components/UI/DropdownMenu";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";

const TaxConfigurationPage = () => {
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const token = getToken(); // Function to retrieve the auth token
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const API_BASE_URL = "https://hrmsnew.prepstripe.com/api/payroll";

  const initialFormData = {
    type: "",
    code: "",
    name: "",
    jurisdiction_country: "IN",
    jurisdiction_state: "",
    effective_from: "",
    effective_to: "",
    rate_type: "SLAB",
    surcharge_percent: 0,
    cess_percent: 0,
    is_active: true,
    version: 1,
    metadata: {
      notes: "",
      source: "",
    },
    slabs: [],
  };

  const [formData, setFormData] = useState(initialFormData);

  const toLocalDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    // Format as YYYY-MM-DD in *local* time
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Configure fetch headers
  const api = useMemo(() => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  // Update form state
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr + "T00:00:00Z").toISOString();
  };

  // Update nested metadata
  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, [name]: value },
    }));
  };

  // Slabs management
  const addSlab = () => {
    setFormData((prev) => ({
      ...prev,
      slabs: [
        ...prev.slabs,
        {
          min_amount: "",
          max_amount: "",
          rate_value: "",
          rate_type: "PERCENT",
          calc_base: "TAXABLE",
          priority: prev.slabs.length,
        },
      ],
    }));
  };

  const updateSlab = (index, field, value) => {
    const updated = [...formData.slabs];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, slabs: updated }));
  };

  const removeSlab = (index) => {
    setFormData((prev) => ({
      ...prev,
      slabs: prev.slabs.filter((_, i) => i !== index),
    }));
  };

  // Submit form
  const createTaxConfig = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        ...formData,
        is_active: formData.is_active ? 1 : 0, // ✅ convert to 1/0
      };
      const response = await api.post(`${API_BASE_URL}/tax-configs`, payload);
      if (![200, 201].includes(response.status)) {
        throw new Error(`Failed to create. Status: ${response.status}`);
      }
      setSuccess("Tax configuration created successfully");
      setNotification({
        message:
          response?.data.message || "Tax configuration created successfully",
        type: "success",
      });
      setShowAddModal(false);
      setFormData(initialFormData); // reset form
      fetchTaxConfigs();
    } catch (err) {
      let errorMessage = "Failed to create tax configuration.";
      setFormErrors({}); // reset old errors

      if (err.response) {
        const data = err.response.data;

        if (data.message) {
          errorMessage = data.message;
        }

        // Capture validation errors
        if (data.errors) {
          setFormErrors(data.errors);
        }
      }

      setError(errorMessage);
      setNotification({
        message: errorMessage,
        type: "error",
      });
      console.error("Create Tax Config Error:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  const updateTaxConfig = async () => {
    if (!editingId) return;

    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        ...formData,
        is_active: formData.is_active ? 1 : 0, // backend expects 0/1
        // effective_from: formData.effective_from + "T00:00:00Z",
        // effective_to: formData.effective_to + "T00:00:00Z",
        effective_from: normalizeDate(formData.effective_from),
        effective_to: normalizeDate(formData.effective_to),
      };

      const response = await api.put(`/tax-configs/${editingId}`, payload);

      if (response.status !== 200) {
        throw new Error(`Failed to update. Status: ${response.status}`);
      }

      console.log("Update Tax Config Response:", response);

      setNotification({
        message:
          response?.data.message || "Tax configuration updated successfully",
        type: "success",
      });

      setSuccess("Tax configuration updated successfully");
      setShowEditModal(false);
      setEditingId(null);
      setFormData(initialFormData); // reset form
      fetchTaxConfigs();
    } catch (err) {
      let errorMessage = "Failed to Update tax configuration.";
      setFormErrors({}); // reset old errors

      if (err.response) {
        const data = err.response.data;

        if (data.message) {
          errorMessage = data.message;
        }

        // Capture validation errors
        if (data.errors) {
          setFormErrors(data.errors);
        }
      }

      setError(errorMessage);
      setNotification({
        message: errorMessage,
        type: "error",
      });
      console.error("Update Tax Config Error:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxConfigs = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get(
        `${API_BASE_URL}/tax-configs?page=${page}`,
      );

      console.log("Fetch Tax Configs Response:", response);

      // Extract tax configs safely
      const taxConfigs = response?.data?.data?.data ?? [];

      setTaxConfigs(taxConfigs);
      setPagination({
        currentPage: taxConfigs.data?.data.current_page || 1,
        totalPages: taxConfigs.data?.data.last_page || 1,
        totalItems: taxConfigs.data?.data.total || 0,
        perPage: taxConfigs.data?.data.per_page || 10,
      });
    } catch (err) {
      setError("Failed to fetch tax configurations: " + err.message);

      // Fallback mock data
      setTaxConfigs([
        {
          id: 1,
          name: "Federal Income Tax",
          tax_rate: 22.5,
          description: "Federal income tax configuration",
          created_at: "2024-01-15T10:30:00Z",
          updated_at: "2024-02-01T14:20:00Z",
        },
        {
          id: 2,
          name: "State Tax",
          tax_rate: 8.5,
          description: "State tax configuration",
          created_at: "2024-01-20T09:15:00Z",
          updated_at: "2024-01-25T16:45:00Z",
        },
        {
          id: 3,
          name: "Social Security",
          tax_rate: 6.2,
          description: "Social Security tax configuration",
          created_at: "2024-01-10T11:00:00Z",
          updated_at: "2024-01-30T13:30:00Z",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxConfigById = async (id) => {
    setShowDetailsModal(true);
    setLoading(true);
    setError("");
    try {
      const response = await api.get(`${API_BASE_URL}/tax-configs/${id}`);

      console.log("Fetch Tax Config By ID Response:", response);

      // Extract tax config object safely
      const config = response?.data?.data ?? null;

      if (!config) {
        throw new Error("No tax configuration found for this ID");
      }

      setSelectedConfig(config);
    } catch (err) {
      setError("Failed to fetch tax configuration details: " + err.message);

      // Mock fallback data
      const mockConfig = {
        id,
        name: id === 2 ? "State Tax" : "Federal Income Tax",
        tax_rate: id === 2 ? 8.5 : 22.5,
        description:
          id === 2
            ? "State tax configuration with detailed settings"
            : "Federal income tax configuration with advanced options",
        created_at: "2024-01-15T10:30:00Z",
        updated_at: "2024-02-01T14:20:00Z",
        status: "active",
        applies_to: "all_employees",
        calculation_method: "percentage",
      };
      setSelectedConfig(mockConfig);
    } finally {
      setLoading(false);
    }
  };

  // Delete tax configuration - DELETE /api/payroll/tax-configs/{id}
  const deleteTaxConfig = async (id) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      // Corrected endpoint
      const response = await api.delete(`${API_BASE_URL}/tax-configs/${id}`);

      console.log("Delete Tax Config Response:", response);

      // If backend returns success status
      if (response.status !== 200 && response.status !== 204) {
        throw new Error(`Failed to delete. Status: ${response.status}`);
      }

      setNotification({
        message:
          response?.data.message || "Tax configuration deleted successfully",
        type: "success",
      });

      setSuccess("Tax configuration deleted successfully");
      setTaxConfigs(taxConfigs.filter((config) => config.id !== id));

      if (selectedConfig && selectedConfig.id === id) {
        setSelectedConfig(null);
      }

      setShowModal(false);
      setDeleteId(null);
    } catch (err) {
      setError("Failed to delete tax configuration: " + err.message);

      // Demo fallback: still remove from UI
      setTaxConfigs(taxConfigs.filter((config) => config.id !== id));
      setSuccess("Tax configuration deleted (demo mode)");

      setShowModal(false);
      setDeleteId(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedConfig(null);
    setFormData(); // reset form
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  useEffect(() => {
    fetchTaxConfigs();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchTaxConfigs(newPage);
    }
  };

  // Actions menu component for table rows
  const ActionsColumn = React.memo(({ row }) => {
    const buttonRef = useRef(null);

    return (
      <div className="flex justify-center items-center relative">
        <button
          ref={buttonRef}
          onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === row.id && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            {/* Edit */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFormData({
                  ...row,
                  is_active: row.is_active ? 1 : 0,
                  effective_from: toLocalDate(row.effective_from),
                  effective_to: toLocalDate(row.effective_to),
                  metadata: row.metadata || { notes: "", source: "" },
                  slabs: row.slabs || [],
                });
                setEditingId(row.id);
                setShowEditModal(true);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Edit
            </button>

            {/* Delete */}
            <DeleteButton
              style="text-red-600 hover:text-red-800 px-3 py-2"
              onDelete={() => {
                deleteTaxConfig(row.id);
                setOpenMenu(null);
              }}
            />
          </DropdownMenu>
        )}
      </div>
    );
  });

  const handleOpenAddModal = () => {
    setFormData(initialFormData); // Reset the form data to initial state
    setShowAddModal(true);
    setShowEditModal(false); // Ensure the edit modal is closed
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleOpenEditModal = (config) => {
    setFormData({
      ...config,
      effective_from: normalizeDate(config.effective_from),
      effective_to: normalizeDate(config.effective_to),
      metadata: config.metadata || { notes: "", source: "" },
      slabs: config.slabs || [],
    });
    setEditingId(config.id);
    setShowEditModal(true);
    setShowAddModal(false); // Ensure the add modal is closed
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  return (
    <div className="">
      {/* notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="px-4 py-4">
        {/* Header */}
        <div className="bg-white mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Tax Configurations
              </h1>
              <p className="text-gray-600">
                Manage and configure payroll tax settings
              </p>
            </div>
            <div className="flex gap-3">
              {/* <button
                        onClick={fetchTaxConfigs}
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button> */}
              <button
                onClick={handleOpenAddModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>
        </div>

        {/* data table */}
        <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
          <table className="min-w-full table-auto md:table-fixed text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
              <tr>
                <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-gray-700 dark:text-gray-300">
                  Name
                </th>
                {/* <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-gray-700 dark:text-gray-300">Tax Rate (%)</th> */}
                {/* <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-gray-700 dark:text-gray-300">Description</th> */}
                {/* <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-gray-700 dark:text-gray-300">Created</th> */}
                <th className="whitespace-nowrap text-center px-6 py-4 font-semibold text-gray-700 dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {taxConfigs.map((config, index) => (
                <tr
                  key={config.id}
                  className={`border-b border-gray-200 transition-all cursor-pointer ${
                    selectedConfig?.id === config.id
                      ? "bg-blue-50 border-blue-300"
                      : "hover:bg-gray-50"
                  } ${index % 2 === 0 ? "bg-white" : "bg-gray-25"}`}
                >
                  <td
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchTaxConfigById(config.id);
                    }}
                    className="py-3 px-4"
                  >
                    <div className="font-medium text-gray-900">
                      {config.name ||
                        config.tax_name ||
                        `Tax Config ${config.id}`}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <ActionsColumn
                      row={config}
                      openMenu={openMenu}
                      setOpenMenu={setOpenMenu}
                      onEdit={updateTaxConfig}
                      onDelete={deleteTaxConfig}
                      // disabled={formLoading}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* pagination compo */}
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName="opacity-50 cursor-not-allowed"
          disabledLinkClassName="pointer-events-none"
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-4 font-poppins"
          pageClassName="mx-1 border rounded dark:text-white font-poppins"
          pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
        />

        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-md max-w-3xl w-full p-4 overflow-y-auto max-h-[90vh]">
              {/* Header */}
              <div className="flex justify-between items-center pb-3 mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  Add New Tax Configuration
                </h3>
                {/* <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                    </button> */}
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.name[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code
                  </label>
                  <input
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.code && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.code[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    placeholder="e.g. income_tax"
                    required
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.type && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.type[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Effective From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="effective_from"
                    value={formData.effective_from}
                    onChange={handleChange}
                    required
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.effective_from && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.effective_from[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Effective To
                  </label>
                  <input
                    type="date"
                    name="effective_to"
                    value={formData.effective_to}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.effective_to && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.effective_to[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cess (%)
                  </label>
                  <input
                    type="number"
                    name="cess_percent"
                    value={formData.cess_percent}
                    onChange={handleChange}
                    min={0}
                    max={100}
                    required
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.cess_percent && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.cess_percent[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surcharge (%)
                  </label>
                  <input
                    type="number"
                    name="surcharge_percent"
                    value={formData.surcharge_percent}
                    onChange={handleChange}
                    min={0}
                    max={100}
                    required
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.surcharge_percent && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.surcharge_percent[0]}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <input
                    name="notes"
                    value={formData.metadata.notes}
                    onChange={handleMetadataChange}
                    className="border p-2 rounded w-full"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source
                  </label>
                  <input
                    name="source"
                    value={formData.metadata.source}
                    onChange={handleMetadataChange}
                    className="border p-2 rounded w-full"
                  />
                </div>
              </div>

              {/* Slabs Section */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2">Tax Slabs</h4>
                {formData.slabs.map((slab, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-4 gap-2 items-center mb-2"
                  >
                    <div>
                      <label className="text-xs text-gray-600">Min</label>
                      <input
                        type="number"
                        value={slab.min_amount}
                        onChange={(e) =>
                          updateSlab(index, "min_amount", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Max</label>
                      <input
                        type="number"
                        value={slab.max_amount}
                        onChange={(e) =>
                          updateSlab(index, "max_amount", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      />
                      {formErrors[`slabs.${index}.max_amount`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.max_amount`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Rate</label>
                      <input
                        type="number"
                        value={slab.rate_value}
                        onChange={(e) =>
                          updateSlab(index, "rate_value", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      />
                      {formErrors[`slabs.${index}.rate_value`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.rate_value`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Rate Type</label>
                      <select
                        value={slab.rate_type}
                        onChange={(e) =>
                          updateSlab(index, "rate_type", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      >
                        <option value="PERCENT">Percent</option>
                        <option value="FIXED">Fixed</option>
                      </select>
                      {formErrors[`slabs.${index}.rate_type`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.rate_type`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Calc Base</label>
                      <select
                        value={slab.calc_base}
                        onChange={(e) =>
                          updateSlab(index, "calc_base", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      >
                        <option value="TAXABLE">Taxable</option>
                        <option value="GROSS">Gross</option>
                        <option value="NET">Net</option>
                      </select>
                      {formErrors[`slabs.${index}.calc_base`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.calc_base`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Priority</label>
                      <input
                        type="number"
                        value={slab.priority}
                        readOnly
                        className="border p-2 rounded bg-gray-100 text-gray-500 w-full"
                      />
                      {formErrors[`slabs.${index}.priority`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.priority`][0]}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeSlab(index)}
                      className="text-red-500 hover:text-red-700 mt-5"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={addSlab}
                  className="mt-2 px-4 py-2 bg-white font-medium border border-blue-500 text-blue-500 rounded-lg"
                >
                  Add Slab
                </button>
              </div>

              {/* Is Active */}
              <div className="flex items-center gap-3 mt-6">
                <label className="text-gray-700 font-medium">Active:</label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                  className="w-5 h-5 accent-green-600"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-7 py-2 text-black hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={createTaxConfig}
                  disabled={loading}
                  className="flex items-center gap-2 px-7 py-2 bg-blue-600 font-medium text-white rounded-md hover:bg-blue-700"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full p-4 overflow-y-auto max-h-[90vh]">
              {/* Header */}
              <div className="flex justify-between items-center  pb-3 mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  Edit Tax Configuration
                </h3>
                {/* <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                    </button> */}
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>

              {/* Form fields — same structure as Add modal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Example field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.name[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code
                  </label>
                  <input
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.code && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.code[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    placeholder="e.g. income_tax"
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.type && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.type[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Effective From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="effective_from"
                    value={formData.effective_from}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.effective_from && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.effective_from[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Effective To
                  </label>
                  <input
                    type="date"
                    name="effective_to"
                    value={formData.effective_to}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.effective_to && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.effective_to[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cess (%)
                  </label>
                  <input
                    type="number"
                    name="cess_percent"
                    value={formData.cess_percent}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.cess_percent && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.cess_percent[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surcharge (%)
                  </label>
                  <input
                    type="number"
                    name="surcharge_percent"
                    value={formData.surcharge_percent}
                    onChange={handleChange}
                    className="border p-2 rounded w-full"
                  />
                  {formErrors.cess_percent && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.cess_percent[0]}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <input
                    name="notes"
                    value={formData.metadata.notes}
                    onChange={handleMetadataChange}
                    className="border p-2 rounded w-full"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source
                  </label>
                  <input
                    name="source"
                    value={formData.metadata.source}
                    onChange={handleMetadataChange}
                    className="border p-2 rounded w-full"
                  />
                </div>
              </div>

              {/* Slabs Section */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2">Tax Slabs</h4>
                {formData.slabs.map((slab, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-4 gap-1 items-center mb-2"
                  >
                    <div>
                      <label className="text-xs text-gray-600">Min</label>
                      <input
                        type="number"
                        value={slab.min_amount}
                        onChange={(e) =>
                          updateSlab(index, "min_amount", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      />
                      {formErrors[`slabs.${index}.min_amount`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.min_amount`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Max</label>
                      <input
                        type="number"
                        value={slab.max_amount}
                        onChange={(e) =>
                          updateSlab(index, "max_amount", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      />
                      {formErrors[`slabs.${index}.max_amount`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.max_amount`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Rate</label>
                      <input
                        type="number"
                        value={slab.rate_value}
                        onChange={(e) =>
                          updateSlab(index, "rate_value", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      />
                      {formErrors[`slabs.${index}.rate_value`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.rate_value`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Rate Type</label>
                      <select
                        value={slab.rate_type}
                        onChange={(e) =>
                          updateSlab(index, "rate_type", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      >
                        <option value="PERCENT">Percent</option>
                        <option value="FIXED">Fixed</option>
                      </select>
                      {formErrors[`slabs.${index}.rate_type`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.rate_type`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Calc Base</label>
                      <select
                        value={slab.calc_base}
                        onChange={(e) =>
                          updateSlab(index, "calc_base", e.target.value)
                        }
                        className="border p-2 rounded w-full"
                      >
                        <option value="TAXABLE">Taxable</option>
                        <option value="GROSS">Gross</option>
                        <option value="NET">Net</option>
                      </select>
                      {formErrors[`slabs.${index}.calc_base`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors[`slabs.${index}.calc_base`][0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Priority</label>
                      <input
                        type="number"
                        value={slab.priority}
                        readOnly
                        className="border p-2 rounded bg-gray-100 text-gray-500 w-full"
                      />
                    </div>
                    <button
                      onClick={() => removeSlab(index)}
                      className="text-red-500 hover:text-red-700 mt-5"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={addSlab}
                  className="mt-2 px-4 py-2 bg-white  border border-blue-500 text-blue-500 rounded-md font-semibold"
                >
                  + Add Slab
                </button>
              </div>

              {/* Is Active */}
              <div className="flex items-center gap-3 mt-6">
                <label className="text-gray-700 font-medium">Active:</label>
                <input
                  type="checkbox"
                  checked={!!formData.is_active} // ✅ works with true/false
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: e.target.checked, // ✅ keep as boolean
                    }))
                  }
                  className="w-5 h-5 accent-green-600"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-7 py-2 text-black hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={updateTaxConfig}
                  disabled={loading}
                  className="px-7 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDetailsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white px-4 py-4 flex justify-between items-center rounded-t-lg">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Tax Configuration Details
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    View and manage configuration settings
                  </p>
                </div>
                {/* <button
                    onClick={closeDetailsModal}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                    >
                    <X className="w-6 h-6" />
                    </button> */}
                <button
                  type="button"
                  onClick={closeDetailsModal}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4">
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                    <p className="text-gray-600 text-sm">Loading details...</p>
                  </div>
                ) : selectedConfig ? (
                  <div className="space-y-6">
                    {/* Header Card */}
                    <div className="bg-gray-200 p-6 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-2xl mb-2">
                            {selectedConfig.name || "N/A"}
                          </h3>
                          <p className=" text-sm">
                            Code: {selectedConfig.code}
                          </p>
                          {/* <p className="text-blue-100 text-sm">ID: {selectedConfig.id}</p> */}
                        </div>
                        <div className="text-right">
                          <p className="text-sm">Rate Type</p>
                          <p className="text-2xl font-bold">
                            {selectedConfig.rate_type || "N/A"}
                          </p>
                          <div
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                              selectedConfig.is_active
                                ? "bg-green-100 bg-opacity-20 text-green-100"
                                : "bg-red-100 bg-opacity-20 text-red-100"
                            }`}
                          >
                            {selectedConfig.is_active ? "Active" : "Inactive"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Basic Information Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <label className="text-sm font-medium text-blue-700 block mb-1">
                          Tax Type
                        </label>
                        <p className="text-gray-800 font-medium capitalize">
                          {selectedConfig.type?.replace(/_/g, " ")}
                        </p>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <label className="text-sm font-medium text-blue-700 block mb-1">
                          Version
                        </label>
                        <p className="text-gray-800 font-medium">
                          v{selectedConfig.version}
                        </p>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <label className="text-sm font-medium text-blue-700 block mb-1">
                          Jurisdiction
                        </label>
                        <p className="text-gray-800 font-medium">
                          {selectedConfig.jurisdiction_country}
                          {selectedConfig.jurisdiction_state &&
                            ` - ${selectedConfig.jurisdiction_state}`}
                        </p>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <label className="text-sm font-medium text-blue-700 block mb-1">
                          Effective From
                        </label>
                        <p className="text-gray-800">
                          {selectedConfig.effective_from &&
                            new Date(
                              selectedConfig.effective_from,
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                        </p>
                      </div>
                    </div>

                    {/* Tax Rates Section */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">
                        Tax Rates & Charges
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded border">
                          <label className="text-sm font-medium text-gray-600 block mb-1">
                            Cess
                          </label>
                          <p className="text-xl font-bold text-gray-800">
                            {selectedConfig.cess_percent}%
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <label className="text-sm font-medium text-gray-600 block mb-1">
                            Surcharge
                          </label>
                          <p className="text-xl font-bold text-gray-800">
                            {selectedConfig.surcharge_percent}%
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <label className="text-sm font-medium text-gray-600 block mb-1">
                            Rate Value
                          </label>
                          <p className="text-xl font-bold text-gray-800">
                            {selectedConfig.rate_value || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Tax Slabs Section */}
                    {selectedConfig.slabs &&
                      selectedConfig.slabs.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-md">
                          <h4 className="text-lg font-semibold text-gray-800 mb-4">
                            Tax Slabs
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse bg-white rounded-md border">
                              <thead>
                                <tr className="bg-blue-50">
                                  <th className="text-left py-2 px-3 font-semibold text-blue-800 border-b">
                                    Min Amount
                                  </th>
                                  <th className="text-left py-2 px-3 font-semibold text-blue-800 border-b">
                                    Max Amount
                                  </th>
                                  <th className="text-left py-2 px-3 font-semibold text-blue-800 border-b">
                                    Rate (%)
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedConfig.slabs.map((slab, index) => (
                                  <tr
                                    key={slab.id}
                                    className={
                                      index % 2 === 0
                                        ? "bg-white"
                                        : "bg-gray-50"
                                    }
                                  >
                                    <td className="py-2 px-3 border-b">
                                      ₹
                                      {parseFloat(
                                        slab.min_amount,
                                      ).toLocaleString("en-IN")}
                                    </td>
                                    <td className="py-2 px-3 border-b">
                                      {slab.max_amount
                                        ? `₹${parseFloat(slab.max_amount).toLocaleString("en-IN")}`
                                        : "Above"}
                                    </td>
                                    <td className="py-2 px-3 border-b font-semibold text-blue-600">
                                      {slab.rate}%
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                    {/* Metadata Section */}
                    {selectedConfig.metadata && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4">
                          Additional Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white p-3 rounded border">
                            <label className="text-sm font-medium text-gray-600 block mb-1">
                              Source
                            </label>
                            <p className="text-gray-800 font-medium">
                              {selectedConfig.metadata.source}
                            </p>
                          </div>
                          <div className="bg-white p-3 rounded border">
                            <label className="text-sm font-medium text-gray-600 block mb-1">
                              Notes
                            </label>
                            <p className="text-gray-800">
                              {selectedConfig.metadata.notes}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Timestamps */}
                    {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <label className="text-sm font-medium text-blue-700 block mb-1">Created</label>
                                <p className="text-gray-800">
                                {new Date(selectedConfig.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">By User ID: {selectedConfig.created_by}</p>
                            </div>

                            <div className="bg-blue-50 p-4 rounded-lg">
                                <label className="text-sm font-medium text-blue-700 block mb-1">Last Updated</label>
                                <p className="text-gray-800">
                                {new Date(selectedConfig.updated_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">By User ID: {selectedConfig.updated_by}</p>
                            </div>
                        </div> */}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4">
                      {/* <button
                            onClick={() => handleDeleteClick(selectedConfig.id)}
                            className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete Configuration
                        </button> */}
                      <button
                        onClick={closeDetailsModal}
                        className="px-7 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No configuration data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Confirm Delete
                </h3>
                <p className="text-gray-600">
                  Are you sure you want to delete this tax configuration? This
                  action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setDeleteId(null);
                  }}
                  className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteTaxConfig(deleteId)}
                  disabled={loading}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxConfigurationPage;
